import UserNotifications from "../../services/api/user-notifications";
import BrowserNotifications from "@frontend/browser-notifications";

const initState = () => ({
  needsLoading: true,
  isLoading: false,
  notifications: [],
  unreadCount: 0,
  csTaskNotificationsEnabled: true,
})

export const MUTATIONS = {
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  SET_UNREAD_COUNT: 'SET_UNREAD_COUNT',
  SET_IS_LOADING: 'SET_IS_LOADING',
  SET_NEEDS_LOADING: 'SET_NEEDS_LOADING',
  SET_CS_TASK_NOTIFICATIONS_ENABLED: 'SET_CS_TASK_NOTIFICATIONS_ENABLED',
}

const normalizeNotification = (notification) => ({
    ...notification,
    data: JSON.parse(notification.data),
    created_at: notification.created_at ? new Date(notification.created_at) : null,
    read_at: notification.read_at ? new Date(notification.read_at) : null,
});

const showBrowserNotification = async function (notification) {
  await BrowserNotifications.showNotification(
    'It\'s Rapid',
    {
      body: notification?.data?.message || 'You have a new notification.',
      icon: '/favicon.ico'
    }
  )
}

export default {
  namespaced: true,
  state: initState(),
  getters: {
    needsLoading: state => state.needsLoading,
    isLoading: state => state.isLoading,
    notifications: state => state.notifications,
    unreadCount: state => state.unreadCount,
    csTaskNotificationsEnabled: state => state.csTaskNotificationsEnabled,
  },
  mutations: {
    [MUTATIONS.SET_NOTIFICATIONS](state, payload) {
      state.notifications = payload;
    },
    [MUTATIONS.SET_UNREAD_COUNT](state, payload) {
      state.unreadCount = payload;
    },
    [MUTATIONS.SET_IS_LOADING](state, payload) {
      state.isLoading = payload;
    },
    [MUTATIONS.SET_NEEDS_LOADING](state, payload) {
      state.needsLoading = payload;
    },
    [MUTATIONS.SET_CS_TASK_NOTIFICATIONS_ENABLED](state, payload) {
      state.csTaskNotificationsEnabled = !!payload;
    },
  },
  actions: {
    async fetchNotifications({ commit }) {
      commit(MUTATIONS.SET_IS_LOADING, true);
      const notificationsData = await UserNotifications.getNotifications();
      const countData = await UserNotifications.getUnreadCount();

      const notifications = notificationsData.data.data.map(notification => {
        return normalizeNotification(notification);
      });
      commit(MUTATIONS.SET_NOTIFICATIONS, notifications);
      commit(MUTATIONS.SET_UNREAD_COUNT, countData.data.count);
      commit(MUTATIONS.SET_IS_LOADING, false);
      commit(MUTATIONS.SET_NEEDS_LOADING, false);
    },
    async fetchNotificationsCount({ commit }) {
      const countData = await UserNotifications.getUnreadCount();
      commit(MUTATIONS.SET_UNREAD_COUNT, countData.data.count);
    },
    async replaceIdentifiedNotification({ commit, state }, payload) {
      const notification = normalizeNotification(payload);

      if (!notification.data?.id) {
        return;
      }

      let unreadCount = state.unreadCount;

      const existing = state.notifications.filter(n => n.data?.id === notification.data.id);
      const notifications = state.notifications.filter(n => n.data?.id !== notification.data.id);

      if (existing.length) {
        const wasRead = existing.some(n => n.read_at);
        if (wasRead && !notification.read_at) {
          unreadCount++;
          await showBrowserNotification(notification);
        }
      } else {
        unreadCount++;
        await showBrowserNotification(notification);
      }

      notifications.unshift(notification);

      commit(MUTATIONS.SET_NOTIFICATIONS, notifications);
      commit(MUTATIONS.SET_UNREAD_COUNT, unreadCount);
      commit(MUTATIONS.SET_NEEDS_LOADING, true);
      return notification;
    },

    addTransientNotification({ commit, state }, payload) {
      const notification = normalizeNotification(payload);
      const rest = notification.data?.id
        ? state.notifications.filter(n => n.data?.id !== notification.data.id)
        : state.notifications;
      commit(MUTATIONS.SET_NOTIFICATIONS, [notification, ...rest]);
    },
    async addNotification({ commit, state, rootGetters }, payload) {
      const notifications = state.notifications;
      const notification = normalizeNotification(payload);
      
      commit(MUTATIONS.SET_NOTIFICATIONS, [notification, ...notifications]);
      commit(MUTATIONS.SET_UNREAD_COUNT, state.unreadCount + 1);
      commit(MUTATIONS.SET_NEEDS_LOADING, true);

      await showBrowserNotification(notification)

      return notification;
    },
    async readNotification({ commit, state }, id) {
      const notifications = state.notifications;
      const targetNotification = notifications.find(notification => notification.id === id);
      if (targetNotification && targetNotification.read_at === null) {
        targetNotification.read_at = new Date();
        
        commit(MUTATIONS.SET_UNREAD_COUNT, state.unreadCount - 1);
        commit(MUTATIONS.SET_NOTIFICATIONS, [...notifications]);
        
        await UserNotifications.read(id);
      }
    },
    async readAllNotifications({ commit, state }) {
      const notifications = state.notifications;
      
      for (const notification of notifications) {
        notification.read_at = new Date();
      }

      commit(MUTATIONS.SET_NOTIFICATIONS, [...notifications]);
      commit(MUTATIONS.SET_UNREAD_COUNT, 0);
      await UserNotifications.readAll();
    },
    async clearAllNotifications({ commit }) {
      await UserNotifications.clearAll();
      commit(MUTATIONS.SET_NOTIFICATIONS, []);
      commit(MUTATIONS.SET_UNREAD_COUNT, 0);
    },
    async fetchNotificationPreferences({ commit }) {
      try {
        const { data } = await UserNotifications.getPreferences();
        commit(MUTATIONS.SET_CS_TASK_NOTIFICATIONS_ENABLED, !!data?.cs_task_notifications_enabled);
      } catch (e) {
        console.warn('Could not load notification preferences', e);
      }
    },
    async updateNotificationPreferences({ commit, state }, enabled) {
      const previous = state.csTaskNotificationsEnabled;
      commit(MUTATIONS.SET_CS_TASK_NOTIFICATIONS_ENABLED, enabled);
      try {
        const { data } = await UserNotifications.updatePreferences({ cs_task_notifications_enabled: enabled });
        commit(MUTATIONS.SET_CS_TASK_NOTIFICATIONS_ENABLED, !!data?.cs_task_notifications_enabled);
      } catch (e) {
        commit(MUTATIONS.SET_CS_TASK_NOTIFICATIONS_ENABLED, previous);
        if (typeof toastr !== 'undefined') {
          toastr.error('Could not update notification preferences');
        }
        throw e;
      }
    },
  }
}
